import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown-menu";
import { useMapStore } from "store/useMapStore";
import { usePostMapAddress } from "hooks/console/map/usePostMapAddress";

interface AddressData {
    cd: string;
    addr_name: string;
    full_addr?: string;
    x?: string;
    y?: string;
}

interface AddressListData {
    list: AddressData[];
}

export default function LocationSelector() {
    const { setMapCenter, setMapZoom } = useMapStore();
    const initialSelectedAddressList = [
        { cd: "", addr_name: "시/도" },
        { cd: "", addr_name: "시/군/구" },
        { cd: "", addr_name: "읍/면/동" },
    ];
    const initialAddressList = [{ list: [] }, { list: [] }, { list: [] }];
    const [selectedAddressList, setSelectedAddressList] = useState<AddressData[]>(initialSelectedAddressList); //선택된 주소리스트
    const [addressList, setAddressList] = useState<AddressListData[]>(initialAddressList);
    const [firstRender, setFirstRender] = useState(false);
    const addressFirstStyle = "min-w-[94px] text-center";
    const addressStyle = twMerge(
        addressFirstStyle,
        " relative after:absolute after:content-[''] after:w-[32px] after:h-full after:-left-[32px] after:top-0 after:bg-arrow_right_g after:bg-no-repeat after:bg-center",
    );
    const { mutate: postMapAddress } = usePostMapAddress(() => {});

    //선택된 주소리스트, 주소리스트 값 변경 함수
    const updateLists = (data: any[], render: number, updatedSelectedList: typeof selectedAddressList, updatedList: typeof addressList) => {
        const idx = render - 1;
        updatedSelectedList[idx] = data[0];
        setSelectedAddressList(updatedSelectedList);
        updatedList[idx].list = data;

        if (render === 1) {
            setFirstRender(true);
        }
    };

    //시도 구군 목록 조회
    const getAddressList = async (code: string, render: number | null, index?: number) => {
        postMapAddress(
            { cd: code },
            {
                onSuccess: data => {
                    const addressData = data.data;

                    const updatedSelectedList = [...selectedAddressList];
                    const updatedList = [...addressList];
                    if (render) {
                        updateLists(addressData, render, updatedSelectedList, updatedList);
                    }
                    if (!render && index) {
                        updatedList[index].list = addressData;
                        setAddressList(updatedList);
                    }
                },
            },
        );
    };

    useEffect(() => {
        getAddressList("", 1);
    }, []);

    //처음 렌더링시 기본값 서울특별시 강남구로 적용
    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);
            const code = selectedAddressList[0].cd;
            getAddressList(code, 2);
        }
    }, [firstRender, selectedAddressList]);

    //드롭다운메뉴 안에 주소 클릭시
    const handleAddressClick = (data: AddressData, index: number) => {
        let updatedSelectedList = [...selectedAddressList];
        let updatedList = [...addressList];

        switch (index) {
            case 0:
                updatedSelectedList = [...initialSelectedAddressList];
                updatedList[1].list = [];
                updatedList[2].list = [];
                setAddressList(updatedList);
                setMapZoom(11);
                break;
            case 1:
                updatedSelectedList[2] = initialSelectedAddressList[2];
                updatedList[2].list = [];
                setAddressList(updatedList);
                setMapZoom(14);
                break;
            default:
                setMapZoom(15);
                break;
        }

        updatedSelectedList[index] = data;
        setSelectedAddressList(updatedSelectedList);

        const mapCenterData = { lat: Number(data.y), lng: Number(data.x) };
        setMapCenter(mapCenterData);
    };

    return (
        <>
            <ul className="fixed z-[1] top-[143px] right-[12px] border border-[#E1E2E4] rounded-[30px] bg-white h-[50px] p-[0_12px_0_44px] flex items-center gap-x-[32px] after:absolute after:content-[''] after:w-[24px] after:h-full after:bg-ic_location after:bg-no-repeat after:bg-center after:top-0 after:left-[12px]">
                {selectedAddressList.map((selectedAddr, i) => {
                    const idx = i - 1;
                    return (
                        <li key={`selected_address_${i}`} className={addressStyle}>
                            <DropdownMenu>
                                {i === 0 || (i > 0 && selectedAddressList[idx] && selectedAddressList[idx].full_addr) ? (
                                    <>
                                        <DropdownMenuTrigger
                                            asChild
                                            className="text-[18px] text-[#878A93] font-[500] aria-expanded:text-[#1B1C1E] aria-expanded:underline pb-[2px]"
                                            onPointerDown={() => {
                                                if (
                                                    selectedAddressList[idx] &&
                                                    selectedAddressList[idx].full_addr &&
                                                    addressList[i].list.length === 0
                                                ) {
                                                    const code = selectedAddressList[idx].cd;
                                                    getAddressList(code, null, i);
                                                }
                                            }}
                                        >
                                            <button type="button">{selectedAddr.addr_name}</button>
                                        </DropdownMenuTrigger>
                                        {addressList[i].list.length > 0 && (
                                            <DropdownMenuContent className="bg-white p-[16px] border-[#E1E2E4] rounded-[8px] mt-[10px]">
                                                <DropdownMenuItem asChild className="grid grid-rows-7 grid-flow-col p-0">
                                                    <ul>
                                                        {addressList[i].list.map((addr, k) => {
                                                            return (
                                                                <li key={`address_${k}`} className="min-w-[120px]">
                                                                    <button
                                                                        type="button"
                                                                        className="hover:text-[#FF7B2E] hover:font-[500]"
                                                                        onClick={() => {
                                                                            handleAddressClick(addr, i);
                                                                        }}
                                                                    >
                                                                        {addr.addr_name}
                                                                    </button>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-[18px] text-[#878A93] font-[500] pb-[2px]">{selectedAddr.addr_name}</p>
                                )}
                            </DropdownMenu>
                        </li>
                    );
                })}
            </ul>
        </>
    );
}
